import { Autocomplete, Chip, TextField } from '@mui/material';
import { SyntheticEvent, useState } from 'react';

import { getSelectOrInputFieldSx } from '../Input.style';
import { LabelContainer, TextFieldContainer } from '../TextInput/TextInput.style';

type ChipListInputProps = {
  id: string;
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  isValidEntry: (entry: string) => boolean;
  invalidEntryMessage: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
};

/**
 * Free text input managing a list of values displayed as removable chips.
 * An entry is added on Enter or comma, and only when it is valid and not already in the list.
 */
export const ChipListInput = ({
  id,
  label,
  values,
  onChange,
  isValidEntry,
  invalidEntryMessage,
  placeholder,
  helperText,
  error,
  disabled,
  required,
}: ChipListInputProps): JSX.Element => {
  const [inputValue, setInputValue] = useState('');
  const [isInputInvalid, setIsInputInvalid] = useState(false);

  const addEntry = (entry: string) => {
    const trimmedEntry = entry.trim();
    if (!trimmedEntry) {
      return;
    }
    if (!isValidEntry(trimmedEntry)) {
      setIsInputInvalid(true);
      return;
    }
    if (!values.includes(trimmedEntry)) {
      onChange([...values, trimmedEntry]);
    }
    setInputValue('');
    setIsInputInvalid(false);
  };

  const handleChange = (_event: SyntheticEvent, newValues: string[], reason: string) => {
    if (reason === 'createOption') {
      addEntry(newValues[newValues.length - 1]);
      return;
    }
    onChange(newValues);
  };

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    setIsInputInvalid(false);
    if (newInputValue.endsWith(',')) {
      addEntry(newInputValue.slice(0, -1));
      return;
    }
    setInputValue(newInputValue);
  };

  return (
    <TextFieldContainer $fullWidth>
      <LabelContainer htmlFor={id}>{label + (required ? '*' : '')}</LabelContainer>
      <Autocomplete
        id={id}
        multiple
        freeSolo
        options={[]}
        value={values}
        inputValue={inputValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onBlur={() => addEntry(inputValue)}
        disabled={disabled}
        disableClearable
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((tagValue, index) => (
            // eslint-disable-next-line react/jsx-key
            <Chip size="small" label={tagValue} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            sx={getSelectOrInputFieldSx()}
            placeholder={disabled ? undefined : placeholder}
            error={error || isInputInvalid}
            helperText={isInputInvalid ? invalidEntryMessage : (helperText ?? ' ')}
          />
        )}
      />
    </TextFieldContainer>
  );
};
